<template>
  <div class="cp-wrap">
    <input
      ref="input"
      :value="value"
      type="text"
      class="cp-input"
      :placeholder="placeholder"
      autocomplete="off"
      @input="onInput($event.target.value)"
      @focus="open = true"
      @blur="onBlur"
    />
    <ul v-if="open && grouped.length" class="cp-list">
      <template v-for="(grp, gi) in grouped">
        <li :key="'h' + gi" class="cp-group-header">{{ grp.group }}</li>
        <li
          v-for="(c, i) in grp.items"
          :key="gi + '|' + i + '|' + (c[field] || '')"
          class="cp-item"
          @mousedown.prevent="pick(c)"
        >
          <span class="cp-name">{{ c.name }}</span>
          <span v-if="c.status" class="cp-status">{{ c.status }}</span>
          <span v-if="lastContactLabel(c)" class="cp-last" :title="'You already contacted this person'">· {{ lastContactLabel(c) }}</span>
          <span class="cp-val">{{ c[field] }}</span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
// Stable role order for the grouped dropdown.
const RANK = {
  Owner: 0, 'Agent Admin': 1, Agent: 2, Vendor: 3, 'Employee Admin': 4, Employee: 5, Client: 6, 'Team Contact': 7,
};

export default {
  name: 'ContactPicker',
  props: {
    value: { type: String, default: '' },
    contacts: { type: Array, default: () => [] }, // [{name,email,phone,status,group}]
    field: { type: String, default: 'email' }, // 'email' | 'phone'
    placeholder: { type: String, default: 'search or type…' },
  },
  data() { return { open: false }; },
  computed: {
    filtered() {
      const q = (this.value || '').toLowerCase().trim();
      const list = this.contacts.filter((c) => c[this.field]);
      if (!q) return list.slice(0, 200);
      return list.filter((c) => `${c.name} ${c[this.field]} ${c.group}`.toLowerCase().includes(q)).slice(0, 200);
    },
    // Group the matches by permission (Owner / Agent Admin / Agent / Vendor /
    // Employee / Client / Team Contact) with section headers.
    grouped() {
      const byGroup = {};
      this.filtered.forEach((c) => {
        const g = c.group || 'Other';
        (byGroup[g] = byGroup[g] || []).push(c);
      });
      return Object.keys(byGroup)
        .sort((a, b) => (RANK[a] ?? 9) - (RANK[b] ?? 9) || a.localeCompare(b))
        .map((group) => ({ group, items: byGroup[group] }));
    },
  },
  methods: {
    onInput(v) { this.open = true; this.$emit('input', v); },
    pick(c) { this.$emit('input', c[this.field]); this.open = false; },
    onBlur() { setTimeout(() => { this.open = false; }, 150); },
    // "emailed May 26" / "texted May 26" — last time WE reached this contact on
    // the current channel, so the operator doesn't double-send.
    lastContactLabel(c) {
      const at = this.field === 'phone' ? c.last_texted_at : c.last_emailed_at;
      if (!at) return '';
      const verb = this.field === 'phone' ? 'texted' : 'emailed';
      const d = new Date(at);
      const opts = d.getFullYear() === new Date().getFullYear()
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' };
      return `${verb} ${d.toLocaleDateString(undefined, opts)}`;
    },
  },
};
</script>

<style scoped>
.cp-wrap { position: relative; flex: 1; min-width: 0; }
/* Self-contained input styling (index.vue's .dt-input is scoped and can't reach here). */
.cp-input {
  width: 100%; box-sizing: border-box; border: 1px solid #d6dbe4; border-radius: 7px;
  padding: 9px 11px; font-size: 14px; font-family: inherit; color: #1c2536; background: #fff;
}
.cp-input:focus { outline: none; border-color: #1b5fd0; }
.cp-input::placeholder { color: #9aa3b2; }
.cp-list { position: absolute; z-index: 20; top: 100%; left: 0; right: 0; margin: 2px 0 0; padding: 4px; list-style: none; background: #fff; border: 1px solid #d6dbe3; border-radius: 8px; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14); max-height: 280px; overflow-y: auto; }
.cp-group-header { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; color: #9aa3b2; padding: 8px 9px 3px; }
.cp-item { display: flex; align-items: center; gap: 8px; padding: 7px 9px; border-radius: 6px; cursor: pointer; font-size: 13px; }
.cp-item:hover { background: #eef4ff; }
.cp-name { font-weight: 600; color: #1c2536; }
.cp-status { font-size: 10px; color: #6b7280; }
.cp-last { font-size: 10px; font-weight: 600; color: #b9770e; white-space: nowrap; }
.cp-val { margin-left: auto; font-size: 12px; color: #9aa3b2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 40%; }
</style>
